// src/components/PrintInvoice.jsx
//
// The customer-facing invoice. Deliberately separate from PrintTicket: that one
// is a workshop docket (make-by dates, design status, card message) and should
// never be handed to a customer. This one is money and contact details only.
//
// Hidden until printing. Which of the two documents comes out is decided by the
// `printing-invoice` class on <body> — see src/index.css.

import { createPortal } from 'react-dom';
import { useAuth } from '../hooks/useAuth';
import { neededDateOf, neededTimeOf, fmtDay } from '../lib/priority';
import {
  invoiceNumberOf, invoiceDateOf, fmtLongDate, customerNameOf,
  invoiceLines, invoiceTotals, paymentSummary, recipientBlock, cancellationOf,
} from '../lib/invoice';

const money = (n) => '$' + (Number(n) || 0).toFixed(2);

export function PrintInvoice({ order }) {
  const { shop } = useAuth();
  if (!order) return null;

  const lines  = invoiceLines(order);
  const tot    = invoiceTotals(order);
  const pay    = paymentSummary(order);
  const cancel = cancellationOf(order);
  const needed = neededDateOf(order);
  const time   = neededTimeOf(order);
  const rec    = recipientBlock(order);
  const cityLine = [shop?.city, shop?.province].filter(Boolean).join(', ');

  // Rendered into <body> rather than inside the app tree. During print the
  // app is hidden with `display:none`, which lets this document start at the
  // top of the page and flow onto further pages — with the old approach the
  // app still occupied space, forcing position:fixed and clipping anything
  // past a single page.
  return createPortal(
    <div className="tk-print-root">
    <div id="print-invoice">
      <div className="inv-head">
        <div>
          <div className="inv-shop">{shop?.name}</div>
          {shop?.address && <div className="inv-shop-sub">{shop.address}</div>}
          {cityLine && <div className="inv-shop-sub">{cityLine}</div>}
          {shop?.phone && <div className="inv-shop-sub">{shop.phone}</div>}
          {shop?.email && <div className="inv-shop-sub">{shop.email}</div>}
        </div>
        <div className="inv-meta">
          <div className="inv-title">{cancel.cancelled ? "CANCELLED" : "INVOICE"}</div>
          <div>{invoiceNumberOf(order)}</div>
          <div>{fmtLongDate(invoiceDateOf(order))}</div>
        </div>
      </div>

      <div className="inv-parties">
        <div>
          <div className="inv-label">Bill To</div>
          <div>{customerNameOf(order)}</div>
          {order.billing?.address && <div>{order.billing.address}</div>}
          {(order.billing?.city || order.billing?.stPr) && (
            <div>{[order.billing.city, order.billing.stPr].filter(Boolean).join(', ')}</div>
          )}
          {order.billing?.phone && <div>{order.billing.phone}</div>}
          {order.billing?.email && <div>{order.billing.email}</div>}
        </div>
        {rec.length > 0 && (
          <div>
            <div className="inv-label">Deliver To</div>
            {rec.map((l, i) => <div key={i}>{l}</div>)}
          </div>
        )}
        <div>
          <div className="inv-label">Order</div>
          <div>#{order.orderNumber}</div>
          {needed && <div>{fmtDay(needed, { withYear: true })}{time ? ` · ${time}` : ''}</div>}
        </div>
      </div>

      <table className="inv-table">
        <thead>
          <tr>
            <th className="inv-qty">Qty</th>
            <th>Description</th>
            <th className="inv-num">Price</th>
            <th className="inv-num">Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i}>
              <td className="inv-qty">{l.qty}</td>
              <td>{l.description}{l.discount ? ` (−${l.discount}%)` : ''}</td>
              <td className="inv-num">{money(l.price)}</td>
              <td className="inv-num">{money(l.ext)}</td>
            </tr>
          ))}
          {lines.length === 0 && (
            <tr><td colSpan={4} style={{ fontStyle: 'italic' }}>No items on this order.</td></tr>
          )}
        </tbody>
      </table>

      <div className="inv-totals">
        <div className="inv-total-row"><span>Subtotal</span><span>{money(tot.subtotal)}</span></div>
        {tot.deliveryFee > 0 && <div className="inv-total-row"><span>Delivery</span><span>{money(tot.deliveryFee)}</span></div>}
        {tot.serviceFee  > 0 && <div className="inv-total-row"><span>Service Fee</span><span>{money(tot.serviceFee)}</span></div>}
        <div className="inv-total-row"><span>Tax</span><span>{money(tot.tax)}</span></div>
        <div className="inv-total-row inv-grand"><span>Total</span><span>{money(tot.total)}</span></div>
        {!pay.settled && (
          <div className="inv-total-row inv-due"><span>Amount Due</span><span>{money(pay.amountDue)}</span></div>
        )}
      </div>

      {/* A cancelled order is not a bill. Without this the customer could be
          emailed an invoice for something that is not happening. */}
      {cancel.cancelled ? (
        <div className="inv-pay">
          <strong>CANCELLED</strong>{cancel.reason ? ` — ${cancel.reason}` : ''}
          {cancel.refundDue && <> · <strong>Refund due {money(cancel.refundAmount)}</strong></>}
          {cancel.refunded && <> · Refunded {money(cancel.refundAmount)}</>}
        </div>
      ) : (
        <div className="inv-pay">
          <strong>Payment:</strong>{' '}
          {pay.method ? <>{pay.method} — </> : null}
          <strong>{pay.label}</strong>
        </div>
      )}

      <div className="inv-foot">
        Thank you for supporting {shop?.name || 'our shop'}.
      </div>
    </div>
    </div>,
    document.body
  );
}
